import React, { useState, useRef } from 'react';
import { useLang } from '../context/LanguageContext';
import PageHero from '../components/PageHero';
import CTASection from '../components/CTASection';
import FormShell, { Field, inputCls } from '../components/FormShell';
import { submitToSheet, generateSubmissionId, getVisitorId, sanitizePhone } from '../lib/api';
import { FileText, ClipboardList, FolderCheck, ShieldCheck } from 'lucide-react';

const icons = [FileText, ClipboardList, FolderCheck, ShieldCheck];

const TenderSupport = () => {
  const { t, lang } = useLang();
  const initial = { full_name: '', email: '', phone: '', country: '', company_name: '', tender_reference: '', additional_notes: '', consent: false };
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [locked, setLocked] = useState(false);
  const [success, setSuccess] = useState(false);
  const [refNo, setRefNo] = useState('');
  const submitLock = useRef(false);
  const set = (k, v) => setForm({ ...form, [k]: v });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.consent || submitLock.current) return;
    submitLock.current = true;
    setSubmitting(true);
    setLocked(true);
    const submission_id = generateSubmissionId('SIB-TND');
    const payload = {
      form_type: 'tender_support',
      submission_id,
      visitor_id: getVisitorId(),
      full_name: form.full_name,
      email: form.email,
      phone: form.phone,
      country: form.country,
      company_name: form.company_name,
      tender_reference: form.tender_reference,
      additional_notes: form.additional_notes,
      language: lang,
      page_source: 'tender-support',
      status: 'submitted'
    };
    await submitToSheet(payload);
    setRefNo(submission_id);
    setSubmitting(false);
    setSuccess(true);
    setTimeout(() => { submitLock.current = false; setLocked(false); }, 4000);
  };
  const handleReset = () => { setSuccess(false); setRefNo(''); setForm(initial); submitLock.current = false; setLocked(false); };

  return (
    <div>
      <PageHero eyebrow={t.tender.eyebrow} title={t.tender.title} desc={t.tender.desc} />
      <section className="bg-white py-20">
        <div className="sib-container">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5 mb-12">
            {t.tender.steps.map((s, i) => {
              const Icon = icons[i];
              return (
                <div key={i} className="bg-[#FBF9F2] border border-[#E8E1CE] rounded-lg p-6 card-hover">
                  <div className="w-11 h-11 rounded-md bg-[#0B5D3B]/10 flex items-center justify-center mb-3"><Icon className="w-5 h-5 text-[#0B5D3B]" /></div>
                  <div className="text-[#D4A840] font-bold text-sm mb-1">{String(i+1).padStart(2,'0')}</div>
                  <h4 className="font-semibold text-[#0B5D3B] mb-1.5 text-[15px]">{s.title}</h4>
                  <p className="text-[13px] text-[#33473d]/80 leading-relaxed">{s.desc}</p>
                </div>
              );
            })}
          </div>
          <div className="max-w-2xl mx-auto">
            <FormShell title={t.tender.cta} desc={t.tender.desc} onSubmit={handleSubmit} submitting={submitting} locked={locked} success={success} onReset={handleReset} refNo={refNo}>
              <div className="grid md:grid-cols-2 gap-4">
                <Field label={t.forms.fullName} required><input required className={inputCls} value={form.full_name} onChange={e => set('full_name', e.target.value)} /></Field>
                <Field label={t.forms.email} required><input required type="email" className={inputCls} dir="ltr" value={form.email} onChange={e => set('email', e.target.value)} /></Field>
                <Field label={t.forms.phone} required>
                  <input
                    required
                    type="tel"
                    inputMode="tel"
                    pattern="[0-9+\s\-()]{6,20}"
                    title={t.forms.phoneHelp}
                    className={inputCls}
                    dir="ltr"
                    value={form.phone}
                    onChange={e => set('phone', sanitizePhone(e.target.value))}
                    onKeyDown={(e) => { const allowed = /[0-9+\s\-()]/; if (e.key.length === 1 && !allowed.test(e.key) && !e.ctrlKey && !e.metaKey) e.preventDefault(); }}
                  />
                </Field>
                <Field label={t.forms.country} required><select required className={inputCls} value={form.country} onChange={e => set('country', e.target.value)}><option value="">{t.forms.selectCountry}</option>{t.forms.countries.map(c => <option key={c}>{c}</option>)}</select></Field>
              </div>
              <Field label={t.forms.companyName}><input className={inputCls} value={form.company_name} onChange={e => set('company_name', e.target.value)} /></Field>
              <Field label="Tender Reference / Portal"><input className={inputCls} placeholder="e.g. Etimad reference" value={form.tender_reference} onChange={e => set('tender_reference', e.target.value)} /></Field>
              <Field label={t.forms.additionalNotes}><textarea className={inputCls} rows={3} value={form.additional_notes} onChange={e => set('additional_notes', e.target.value)} /></Field>
              <label className="flex items-start gap-2 text-sm text-[#33473d]"><input type="checkbox" required checked={form.consent} onChange={e => set('consent', e.target.checked)} className="mt-1" /><span>{t.forms.consent}</span></label>
            </FormShell>
          </div>
        </div>
      </section>
      <CTASection />
    </div>
  );
};
export default TenderSupport;
